import React from "react";
import { useNavigate } from "react-router-dom";
import { CheckIcon } from "@heroicons/react/24/outline";

const Pricing = () => {
  const navigate = useNavigate();

  const plans = {
    free: {
      name: "Free",
      price: "$0",
      period: "/forever",
      features: [
        "50 AI chat requests/month",
        "15 summaries/month",
        "10 detailed AI notes/month",
        "5 test generations/month",
        "Basic note organization",
        "Community support",
      ],
    },
    student: {
      name: "Student",
      price: "$5",
      period: "/per month",
      features: [
        "1,200 AI chat requests",
        "200 document summaries",
        "120 detailed AI notes",
        "90 test generations",
        "Advanced note organization",
        "Priority support",
      ],
      popular: true,
    },
    professional: {
      name: "Professional",
      price: "$25",
      period: "/per month",
      features: [
        "Unlimited AI chat requests",
        "Unlimited document summaries",
        "360 detailed AI notes",
        "270 test generations",
        "Advanced note organization",
        "Priority support",
      ],
    },
  };

  return (
    <div className="min-h-screen bg-dark-bg">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-dark-bg/60 backdrop-blur-xl">
        <div className="max-w-7xl mx-auto">
          <div className="mx-8 flex justify-between items-center h-16">
            <div
              className="flex items-center gap-2"
              onClick={() => navigate("/")}
              role="button"
            >
              <img
                src="/notuilogo-white.png"
                alt="Notui"
                className="h-8 w-auto"
              />
              <span className="text-xl font-bold text-white">Notui</span>
            </div>
            <button
              onClick={() => navigate("/login")}
              className="px-4 py-1.5 text-sm text-gray-300 hover:text-white transition-colors"
            >
              Login
            </button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="pt-24 pb-16 px-4">
        <div className="max-w-7xl mx-auto">
          {/* Header Section */}
          <div className="text-center mb-16">
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
              Simple, Transparent Pricing
            </h1>
            <p className="text-xl text-gray-400 max-w-2xl mx-auto">
              Choose the plan that best fits your needs. All plans include
              access to our core features.
            </p>
          </div>

          {/* Pricing Grid */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-7xl mx-auto">
            {Object.entries(plans).map(([planId, plan]) => (
              <div
                key={planId}
                className={`relative bg-dark-card rounded-xl p-6 ${
                  plan.popular ? "ring-2 ring-blue-500" : ""
                }`}
              >
                {plan.popular && (
                  <span className="absolute -top-3 right-4 bg-blue-500 text-white px-3 py-1 rounded-full text-sm">
                    Most Popular
                  </span>
                )}

                <h3 className="text-2xl font-bold text-white mb-2">
                  {plan.name}
                </h3>
                <div className="flex items-baseline mb-6">
                  <span className="text-4xl font-bold text-white">
                    {plan.price}
                  </span>
                  <span className="text-gray-400 ml-1">{plan.period}</span>
                </div>

                <ul className="space-y-4 mb-8">
                  {plan.features.map((feature, index) => (
                    <li key={index} className="flex items-center text-gray-300">
                      <CheckIcon className="w-5 h-5 text-green-500 mr-2 flex-shrink-0" />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>

                <button
                  onClick={() =>
                    navigate("/signup", { state: { plan: planId } })
                  }
                  className={`w-full py-3 rounded-lg font-medium transition-colors ${
                    planId === "free"
                      ? "bg-gray-700 text-white hover:bg-gray-600"
                      : "bg-blue-500 text-white hover:bg-blue-600"
                  }`}
                >
                  {planId === "free" ? "Get Started" : "Subscribe Now"}
                </button>
              </div>
            ))}
          </div>
        </div>
      </main>
    </div>
  );
};

export default Pricing;
